/**
 * Los avisos que ve la persona mientras arma su postulación.
 *
 * Son la misma verificación que corre el profesional, pero mostrada antes y
 * al revés: en vez de dejar constancia de lo que falló, advierte lo que va a
 * fallar mientras todavía se puede arreglar.
 *
 * Esto se muestra en el wizard, que es donde se crea y se edita la propuesta,
 * y eso acota lo que puede advertir: lo que se vea aquí saldría en el PRIMER
 * informe de verificación. La segunda etapa —el informe final, donde lo que
 * no se subsanó pasa a ser rechazo— no se decide aquí: para entonces la
 * convocatoria ya cerró, la propuesta no se edita y la subsanación se hace
 * por su propio camino.
 *
 * Con una excepción, que no depende de la etapa: los documentos técnicos no
 * son subsanables. Su ausencia es causal de rechazo también en el primer
 * informe, así que se advierte como tal.
 *
 * Los textos son los oficiales, sin reescribir: lo que se advierte aquí es
 * palabra por palabra lo que saldría en el informe. Esto advierte; no decide
 * nada ni marca ningún documento.
 */

import moment from 'moment';
import 'moment/locale/es';
import { verificarDocumentosAdministrativos } from './verificacionAdministrativa';
import { verificarDocumentosTecnicos } from './verificacionTecnicos';
import { documentosDelPerfil } from './documentosDelPerfil';

/** La actividad del cronograma que le pone plazo a lo que se advierte aquí. */
export const CLAVE_PRIMER_INFORME = 'Primer informe de verificación de documentos';

/** Hasta cuándo hay para corregir lo que el primer informe pida subsanar. */
export const CLAVE_PLAZO_SUBSANAR =
  'Plazo para subsanar documentos y presentar observaciones';

const fechaDe = (fechas: any, clave: string, cual: 'inicio' | 'fin' = 'inicio'): any => {
  const actividad = (Array.isArray(fechas) ? fechas : []).find(
    (f: any) => f?.clave === clave
  );
  if (!actividad) return null;

  return cual === 'fin'
    ? actividad.valormax ?? actividad.valormin ?? null
    : actividad.valormin ?? actividad.valormax ?? null;
};

/** Cuándo sale el primer informe, para poder decir hasta cuándo hay margen. */
export const fechaDelPrimerInforme = (fechas: any): any =>
  fechaDe(fechas, CLAVE_PRIMER_INFORME);

/**
 * Hasta cuándo hay para subsanar. Se toma el fin de esa actividad: lo que
 * importa no es cuándo empieza el plazo sino cuándo se cierra.
 */
export const fechaLimiteParaSubsanar = (fechas: any): any =>
  fechaDe(fechas, CLAVE_PLAZO_SUBSANAR, 'fin');

export type Aviso = {
  /** El documento al que se refiere, con el nombre que la persona ve en el listado */
  documento: string;
  /** Qué pasaría hoy: 'subsanar' se puede corregir, 'rechazar' deja la propuesta fuera */
  estado: 'subsanar' | 'rechazar';
  /** El texto oficial, igual al que saldría en el informe */
  texto: string;
  grupo: 'administrativo' | 'tecnico';
  /** El paso del wizard donde se arregla */
  paso: string;
};

export type ResumenAvisos = {
  avisos: Aviso[];
  porSubsanar: number;
  porRechazar: number;
};


/**
 * Qué le pasaría hoy a la postulación.
 *
 * Solo reporta lo que se puede afirmar sin abrir los archivos: lo que no se
 * aportó y los formatos de la plataforma que están sin diligenciar o sin
 * firmar. Un documento ya cargado no se revisa por dentro aquí, así que no
 * aparece: la ausencia de avisos no es un visto bueno.
 */
export const avisosDeLaPostulacion = (
  documentosAdministrativos: any,
  documentosTecnicos: any,
  /** El perfil elegido, contando desde cero, como lo guardan los documentos. */
  tipoPersona?: number | null
): ResumenAvisos => {
  // Lo administrativo es subsanable, y esto se ve antes del primer informe.
  const administrativos: Aviso[] = verificarDocumentosAdministrativos(
    documentosDelPerfil(documentosAdministrativos, tipoPersona)
  ).hallazgos.map((h) => ({
    documento: h.nombre,
    estado: h.estado,
    texto: h.observacion,
    grupo: 'administrativo' as const,
    paso: 'propuesta',
  }));

  const tecnicos: Aviso[] = verificarDocumentosTecnicos(
    documentosDelPerfil(documentosTecnicos, tipoPersona)
  ).map((h) => ({
    documento: h.nombre,
    estado: h.estado,
    texto: h.observacion,
    grupo: 'tecnico' as const,
    paso: 'propuesta',
  }));

  const avisos = [...administrativos, ...tecnicos];

  return {
    avisos,
    porSubsanar: avisos.filter((a) => a.estado === 'subsanar').length,
    porRechazar: avisos.filter((a) => a.estado === 'rechazar').length,
  };
};

/**
 * ¿Ya hay que mostrarle los avisos?
 *
 * Estrenar la postulación y recibir de entrada la lista de lo que le van a
 * pedir subsanar no ayuda: todavía no ha tenido ocasión de llenar nada. Así
 * que en la primera pasada los avisos esperan al último paso, que es donde
 * la revisión tiene sentido.
 *
 * Quien vuelve a editar sí los ve desde el principio, y se sabe porque en
 * esa primera pasada ya guardó el nombre del proyecto o algún enlace, que
 * es justo lo del último paso.
 */
export const hayQueMostrarAvisos = (
  postulacion: any,
  enElUltimoPaso: boolean
): boolean => {
  if (enElUltimoPaso) return true;

  const conNombre = String(postulacion?.nombre_propuesta ?? '').trim() !== '';
  const conEnlaces = (Array.isArray(postulacion?.links) ? postulacion.links : []).length > 0;

  return conNombre || conEnlaces;
};

/** "D de MMMM de YYYY", o cadena vacía si no hay fecha que mostrar. */
export const fechaEnLetras = (valor: any): string => {
  if (!valor) return '';
  const fecha = moment(valor).locale('es');
  return fecha.isValid() ? fecha.format('D [de] MMMM [de] YYYY') : '';
};

/**
 * Cuándo sale el informe y hasta cuándo hay para corregir.
 *
 * Lo dicen por igual el panel de alertas, el radicado y las observaciones de
 * la propuesta, así que la frase vive aquí: si los tres no coincidieran, el
 * participante leería tres plazos distintos para lo mismo.
 *
 * Lo que no se subsana no lleva plazo, porque no hay nada que ofrecer.
 */
export const textoDelPlazoParaSubsanar = (
  fechaDelInforme: any,
  fechaLimiteSubsanar: any,
  /** Falso para los avisos de rechazo, que no tienen plazo. */
  haySubsanables = true
): string => {
  const salida = fechaEnLetras(fechaDelInforme);
  const limite = fechaEnLetras(fechaLimiteSubsanar);

  return [
    salida && `El primer informe de verificación sale el ${salida}.`,
    haySubsanables && limite && `Tendrás plazo para subsanar hasta el ${limite}.`,
  ]
    .filter(Boolean)
    .join(' ');
};

/** Lo que quedó por subsanar, en el orden en que aparece en el listado. */
export const loQueFaltaPorSubsanar = (resumen: ResumenAvisos | null | undefined): Aviso[] =>
  (resumen?.avisos ?? []).filter((aviso) => aviso.estado === 'subsanar');

/**
 * Las observaciones de la propuesta, prediligenciadas con lo que falta.
 *
 * Lo administrativo que no se aportó no impide inscribir —es subsanable—,
 * pero sí tiene que constar. Se escribe el mismo texto que el participante ve
 * en su radicado, que es el oficial de cada criterio, para que quien verifica
 * no tenga que volver a redactarlo y para que los dos lean lo mismo.
 *
 * Esto propone; no decide. El profesional confirma o corrige.
 */
export const observacionesDeLoQueFalta = (
  resumen: ResumenAvisos | null | undefined,
  /** Las del cronograma, para cerrar con el plazo. */
  fechaDelInforme?: any,
  fechaLimiteSubsanar?: any
): string => {
  const renglones = loQueFaltaPorSubsanar(resumen).map((aviso) => aviso.texto);
  if (renglones.length === 0) return '';

  const plazo = textoDelPlazoParaSubsanar(fechaDelInforme, fechaLimiteSubsanar);

  return (plazo ? [...renglones, '', plazo] : renglones).join('\n');
};
